import cloneDeep from 'lodash/cloneDeep';
import type { Module } from 'vuex';
import type { State } from './state';
import type { SearchResultHit } from '@mimir/common/dto/searchResultHit';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { updateItem, getItemFields } from '@mimir/web/util/api';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { ItemState } from '@mimir/common/dto/itemState';
import { getSearchResult } from '@mimir/client-common/util/search';
import type { SearchResult } from '@mimir/common/search/interfaces';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { folders, getFolderFromCache, loadAndSetFolder, setCurrentFolder } from '../globals/folders';
import type { ExportModuleState } from './export-module-state';
import { searchState } from '../globals/searchState';


const exportModuleState: ExportModuleState = {
  savingMdf: false,
  noFolderFound: false,
  loadingSearch: false,
  selectedItem: null,
  loadingMetadata: false,
  searchState: {
    from: 0,
    total: 0,
    hits: []
  }
};

export const exportModule: Module<ExportModuleState, State> = {
  namespaced: true,
  state: cloneDeep(exportModuleState),

  mutations: {
    resetSearch: state => {
      state.searchState.hits = [];
      state.searchState.total = 0;
      state.searchState.from = 0;
    },
    noFolderFound: (state, payload: boolean) => state.noFolderFound = payload,
    savingMdf: (state, payload: boolean) => state.savingMdf = payload,
    loadingSearch: (state, payload: boolean) => state.loadingSearch = payload,
    selectItem: (state, payload: SearchResultHit | null) => {
      state.selectedItem = payload;
    },
    injectPlaceholder: (state, payload: SearchResultHit) => {
      state.searchState.hits.unshift(payload);
    },
    searchResultsReceived: (state, payload: SearchResult) => {
      state.searchState.hits = payload.hits;
      state.searchState.total = payload.total;
      state.searchState.from = payload.from;
    },
    updateMetadata: (state, payload: { itemId: string, metadata: ItemMetaData }) => {
      const { itemId, metadata } = payload;
      const index = state.searchState.hits.findIndex(hit => hit.id === itemId);
      if (index >= 0) {
        const item = state.searchState.hits[index];
        item.metadata = Object.assign({}, metadata);
      }
    },
    loadingMetadata: (state, payload: boolean) => state.loadingMetadata = payload,
  },
  getters: {
    loadingMetadata: state => state.loadingMetadata,
    searchState: state => state.searchState,
    savingMdf: state => state.savingMdf,
    loadingSearch: state => state.loadingSearch,
    items: state => state.searchState.hits,
    selectedItem: state => state.selectedItem,
  },
  actions: {
    injectPlaceholder: (context, payload: SearchResultHit) => {
      context.commit('injectPlaceholder', payload);
      context.commit('selectItem', payload);
    },
    refreshCurrentSearch: context => {
      if (folders.currentFolder !== null) {
        return context.dispatch('setFolderIdAndSearch', folders.currentFolder.id);
      }
    },
    setFolderIdAndSearch: async (context, folderId: string | null) => {
      if (folderId === null) {
        searchState.ops.resetSearch();
        return;
      }

      context.commit('loadingSearch', true);
      try {
        const [foundFolder, searchResult] = await Promise.all([
          setFolder(folderId, context.rootGetters.isFolderTreeActive),
          getSearchResult(globalBackendClient, {
            folderId,
            searchString: `itemState:${ItemState.NEW}`,
            from: 0,
            itemsPerPage: 100 //TODO: Do we realistically foresee more than 100 placeholders in a folder?
          }),
        ]);

        if (!foundFolder) {
          context.commit('noFolderFound', true);
          searchState.ops.resetSearch();
          // In the corner case that the folder is still cached, it's likely another user or API request has removed
          // the folder. We manually still set it so the action strip reflects its selected state. It may be more confusing
          // to the user to remove the invalid folder from the tree when clicking on it, so we leave it in the cache.
          const cached = getFolderFromCache(folderId);
          if (cached) {
            setCurrentFolder(cached);
          }
        } else {
          context.commit('searchResultsReceived', searchResult);
        }
        context.commit('selectItem', null);
      } catch (err) {
        logErrorAndShowToast('An error occurred while attempting to load search result', err);
      } finally {
        context.commit('loadingSearch', false);
      }
    },
    saveMdf: async (context, payload: { itemId: string, metadata: ItemMetaData }) => {
      const { itemId, metadata } = payload;
      context.commit('savingMdf', true);
      try {
        await updateItem(itemId, {
          metadata,
        });
        return true;
      } catch (error) {
        console.warn('Error while saving metadata:', error);
        showToast('Failed to save metadata, contact your system administrator.', TOAST.ERROR);
        return false;
      } finally {
        context.commit('savingMdf', false);
      }
    },
    loadMetadata: async ({commit, state}) => {
      // It is important that this loading state is correctly set. The Metadata component in the UI will not mount
      // until this value is set back to true, so that we have precise control over when the sanitizing metadata occurs.
      // This is to prevent sending multiple postMessages with changed metadata events over postMessage. In our regular
      // UI this is also happening, but there it doesn't matter much. However in this export workflow, the target iframe
      // may not want to get repeated metadata changed messages with partially loaded data.
      commit('loadingMetadata', true);
      if (state.selectedItem === null) {
        return;
      }

      const id = state.selectedItem.id;
      try {
        const partialItem = await getItemFields(state.selectedItem.id, ['metadata']);
        commit('updateMetadata', {
          itemId: id,
          metadata: partialItem.metadata,
        });
      } catch (err) {
        logErrorAndShowToast('An error occurred while attempting to get metadata', err);
      } finally {
        commit('loadingMetadata', false);
      }
      return;
    },
    selectAndLoadItem: async ({ commit, dispatch }, searchHit: SearchResultHit) => {
      commit('selectItem', searchHit);
      await dispatch('loadMetadata');
    },
  },
};


const setFolder = async (folderId: string, isFolderTreeActive: boolean) => {
  // We do not support previewing folders in the /export page.
  const folder = await loadAndSetFolder({ folderId, isFolderTreeActive, previewItemOps: null, showFolderInQuickPreview: false, isRefreshSearch: false });
  return folder !== undefined;
};
